import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Lock, ArrowRight, CheckCircle, ShoppingCart, BookOpen, Target, Clock, FileText } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const KAJABI_PURCHASE_URL = "https://vittafinance.mykajabi.com/offers/p52wxgDo";

const ELEMENT_TILES = [
  { id: 'element-1', title: 'Element 1: The Client Relationship', description: '100 practice questions on client relationships', color: '#14b8a6', bgColor: '#f0fdfa' },
  { id: 'element-2', title: 'Element 2: Regulatory Documentation', description: '100 practice questions on regulatory requirements', color: '#3b82f6', bgColor: '#eff6ff' },
  { id: 'element-3', title: 'Element 3: Types and Features of Derivatives', description: '100 practice questions on derivative instruments', color: '#8b5cf6', bgColor: '#f5f3ff' },
  { id: 'element-4', title: 'Element 4: Derivative Pricing', description: '100 practice questions on pricing methodologies', color: '#f59e0b', bgColor: '#fffbeb' },
  { id: 'element-5', title: 'Element 5: Derivative Trading, Clearing and Settlement', description: '100 practice questions on trading operations', color: '#10b981', bgColor: '#f0fdf4' },
  { id: 'element-6', title: 'Element 6: Speculating, Hedging and Other Investment Strategies', description: '100 practice questions on investment strategies', color: '#ec4899', bgColor: '#fdf2f8' },
  { id: 'element-7', title: 'Element 7: Integrity in the Derivative Markets', description: '100 practice questions on market integrity', color: '#6366f1', bgColor: '#eef2ff' },
  { id: 'element-8', title: 'Element 8: Standards of Conduct and Conflicts of Interest', description: '100 practice questions on professional standards', color: '#f97316', bgColor: '#fff7ed' },
];

const DerivativesElementQuestionBankSalesPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const tokenFromUrl = searchParams.get('token');
  
  const [hasAccess, setHasAccess] = useState(false);
  const [checkingAccess, setCheckingAccess] = useState(false);
  const [showAccessForm, setShowAccessForm] = useState(false);
  const [token, setToken] = useState('');
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');

  const PRICE = 49.99;

  useEffect(() => {
    if (tokenFromUrl) {
      verifyAccess(tokenFromUrl);
    }
  }, [tokenFromUrl]);

  const verifyAccess = async (accessToken, userEmail = '') => {
    setCheckingAccess(true);
    setError('');
    
    try {
      const params = new URLSearchParams({
        token: accessToken,
        ...(userEmail && { email: userEmail })
      });
      
      const response = await fetch(
        `${API_URL}/api/books/derivatives-element-bank/check-access?${params}`
      );
      
      if (response.ok) {
        const data = await response.json();
        if (data.valid) {
          setHasAccess(true);
          setShowAccessForm(false);
          localStorage.setItem('derivatives_element_access_token', accessToken);
          if (userEmail) {
            localStorage.setItem('derivatives_element_access_email', userEmail);
          }
          setSearchParams({ token: accessToken });
        } else {
          setError('Invalid access code or email. Please check and try again.');
        }
      } else {
        setError('Unable to verify access. Please try again.');
      }
    } catch (error) {
      console.error('Error checking access:', error);
      setError('Connection error. Please try again.');
    } finally {
      setCheckingAccess(false);
    }
  };

  const handleAccessSubmit = (e) => {
    e.preventDefault();
    if (!email.trim()) {
      setError('Please enter your email address.');
      return;
    }
    if (!token.trim()) {
      setError('Please enter your access code.');
      return;
    }
    verifyAccess(token.trim(), email.trim());
  };

  const handleElementClick = (elementId) => {
    if (!hasAccess) return;
    const url = `/books/derivatives-element-practice/${elementId}?token=${tokenFromUrl || token}`;
    window.open(url, '_blank');
  };

  if (hasAccess) {
    return (
      <div style={{ minHeight: '100vh', background: '#f8f9fa' }}>
        {/* Success Header */}
        <div style={{
          background: 'linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%)',
          padding: '120px 24px 60px',
          textAlign: 'center',
          color: 'white'
        }}>
          <div style={{ maxWidth: '800px', margin: '0 auto' }}>
            <div style={{
              width: '80px',
              height: '80px',
              borderRadius: '50%',
              background: 'rgba(255,255,255,0.2)',
              backdropFilter: 'blur(10px)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 24px',
              boxShadow: '0 10px 40px rgba(0,0,0,0.2)'
            }}>
              <CheckCircle size={48} color="white" />
            </div>
            <h1 style={{
              fontSize: '2.5rem',
              fontWeight: '800',
              marginBottom: '16px'
            }}>
              Access Granted!
            </h1>
            <p style={{
              fontSize: '1.125rem',
              opacity: 0.95,
              marginBottom: '32px'
            }}>
              You now have access to all 8 Element Question Banks
            </p>
          </div>
        </div>

        {/* Element Banks Grid */}
        <div style={{ padding: '60px 24px', maxWidth: '1200px', margin: '0 auto' }}>
          <h2 style={{
            fontSize: '2rem',
            fontWeight: '800',
            color: '#1e293b',
            marginBottom: '48px',
            textAlign: 'center'
          }}>
            Your Element Question Banks
          </h2>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))',
            gap: '24px'
          }}>
            {ELEMENT_TILES.map((element, idx) => (
              <div
                key={element.id}
                onClick={() => handleElementClick(element.id)}
                style={{
                  background: 'white',
                  borderRadius: '16px',
                  padding: '28px',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  border: `2px solid ${element.color}20`
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.transform = 'translateY(-4px)';
                  e.currentTarget.style.boxShadow = '0 8px 30px rgba(0,0,0,0.12)';
                  e.currentTarget.style.borderColor = element.color;
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 20px rgba(0,0,0,0.08)';
                  e.currentTarget.style.borderColor = `${element.color}20`;
                }}
              >
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  marginBottom: '16px'
                }}>
                  <div style={{
                    width: '50px',
                    height: '50px',
                    borderRadius: '12px',
                    background: element.bgColor,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <BookOpen size={28} color={element.color} />
                  </div>
                  <h3 style={{
                    fontSize: '1.125rem',
                    fontWeight: '700',
                    color: '#1e293b',
                    margin: 0,
                    flex: 1
                  }}>
                    {element.title}
                  </h3>
                </div>

                <p style={{
                  fontSize: '0.875rem',
                  color: '#64748b',
                  marginBottom: '20px',
                  lineHeight: '1.6'
                }}>
                  {element.description}
                </p>

                <div style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                  color: element.color,
                  fontWeight: '600',
                  fontSize: '0.938rem'
                }}>
                  Start Practice <ArrowRight size={18} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: '#f8f9fa' }}>
      {/* JSON-LD Structured Data for SEO */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{
        __html: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Course",
          "name": "Derivatives Element-Wise Question Bank",
          "description": "800 practice questions across 8 comprehensive elements for Derivatives exam preparation.",
          "provider": {
            "@type": "Organization",
            "name": "Vitta Finance Academy",
            "url": "https://www.vittafinance.ca"
          },
          "offers": {
            "@type": "Offer",
            "price": "49.99",
            "priceCurrency": "CAD",
            "availability": "https://schema.org/InStock",
            "url": "https://www.vittafinance.ca/books/derivatives-element-bank"
          },
          "educationalLevel": "Professional"
        })
      }} />
      
      {/* Hero Section */}
      <div style={{
        background: 'linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%)',
        padding: '120px 24px 80px',
        textAlign: 'center',
        color: 'white'
      }}>
        <div style={{ maxWidth: '900px', margin: '0 auto' }}>
          <div style={{
            display: 'inline-block',
            background: 'rgba(255,255,255,0.15)',
            backdropFilter: 'blur(10px)',
            padding: '12px 24px',
            borderRadius: '100px',
            marginBottom: '24px',
            fontSize: '0.875rem',
            fontWeight: '600',
            letterSpacing: '0.5px'
          }}>
            DERIVATIVES EXAM PREPARATION
          </div>

          <h1 style={{
            fontSize: 'clamp(2.5rem, 5vw, 3.5rem)',
            fontWeight: '900',
            marginBottom: '24px',
            lineHeight: '1.1'
          }}>
            Derivatives Element-Wise Question Bank
          </h1>

          <p style={{
            fontSize: '1.25rem',
            opacity: 0.95,
            marginBottom: '40px',
            lineHeight: '1.7',
            maxWidth: '800px',
            margin: '0 auto 40px'
          }}>
            Master each topic with 800 targeted practice questions across 8 comprehensive elements
          </p>

          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '24px',
            marginBottom: '48px'
          }}>
            <div style={{
              fontSize: '3rem',
              fontWeight: '900'
            }}>
              ${PRICE.toFixed(2)} CAD
            </div>
          </div>

          {!showAccessForm && (
            <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap' }}>
              <a
                href={KAJABI_PURCHASE_URL}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '12px',
                  background: 'white',
                  color: '#8b5cf6',
                  padding: '18px 32px',
                  borderRadius: '12px',
                  fontSize: '1.125rem',
                  fontWeight: '700',
                  textDecoration: 'none',
                  boxShadow: '0 8px 30px rgba(0,0,0,0.15)',
                  transition: 'all 0.3s'
                }}
                onMouseOver={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
                onMouseOut={(e) => e.currentTarget.style.transform = 'translateY(0)'}
              >
                <ShoppingCart size={22} />
                Purchase Question Bank
              </a>
              <button
                onClick={() => setShowAccessForm(true)}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '12px',
                  background: 'rgba(255,255,255,0.15)',
                  backdropFilter: 'blur(10px)',
                  color: 'white',
                  border: '2px solid rgba(255,255,255,0.3)',
                  padding: '18px 32px',
                  borderRadius: '12px',
                  fontSize: '1.125rem',
                  fontWeight: '700',
                  cursor: 'pointer',
                  transition: 'all 0.3s'
                }}
                onMouseOver={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.25)'}
                onMouseOut={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.15)'}
              >
                <Lock size={22} />
                Already Purchased?
              </button>
            </div>
          )}

          {showAccessForm && (
            <div style={{
              maxWidth: '500px',
              margin: '0 auto',
              background: 'rgba(255,255,255,0.1)',
              backdropFilter: 'blur(20px)',
              borderRadius: '16px',
              padding: '32px',
              border: '1px solid rgba(255,255,255,0.2)'
            }}>
              <h3 style={{
                fontSize: '1.5rem',
                fontWeight: '700',
                marginBottom: '24px'
              }}>
                Enter Your Details
              </h3>
              <form onSubmit={handleAccessSubmit}>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Email Address"
                  required
                  style={{
                    width: '100%',
                    padding: '16px',
                    borderRadius: '8px',
                    border: '2px solid rgba(255,255,255,0.3)',
                    background: 'rgba(255,255,255,0.15)',
                    color: 'white',
                    fontSize: '1rem',
                    marginBottom: '16px',
                    outline: 'none'
                  }}
                />
                <input
                  type="text"
                  value={token}
                  onChange={(e) => setToken(e.target.value)}
                  placeholder="Access Code"
                  required
                  style={{
                    width: '100%',
                    padding: '16px',
                    borderRadius: '8px',
                    border: '2px solid rgba(255,255,255,0.3)',
                    background: 'rgba(255,255,255,0.15)',
                    color: 'white',
                    fontSize: '1rem',
                    marginBottom: '16px',
                    outline: 'none'
                  }}
                />
                {error && (
                  <p style={{
                    color: '#fecaca',
                    fontSize: '0.875rem',
                    marginBottom: '16px'
                  }}>
                    {error}
                  </p>
                )}
                <button
                  type="submit"
                  disabled={checkingAccess}
                  style={{
                    width: '100%',
                    padding: '16px',
                    borderRadius: '8px',
                    background: 'white',
                    color: '#8b5cf6',
                    fontSize: '1.125rem',
                    fontWeight: '700',
                    border: 'none',
                    cursor: checkingAccess ? 'not-allowed' : 'pointer',
                    opacity: checkingAccess ? 0.7 : 1
                  }}
                >
                  {checkingAccess ? 'Verifying...' : 'Access Question Banks'}
                </button>
              </form>
            </div>
          )}
        </div>
      </div>

      {/* Features Section */}
      <div style={{ padding: '80px 24px', background: 'white' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <h2 style={{
            fontSize: '2.5rem',
            fontWeight: '800',
            color: '#1e293b',
            textAlign: 'center',
            marginBottom: '60px'
          }}>
            What's Included
          </h2>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '32px'
          }}>
            {[
              {
                icon: <Target size={32} />,
                title: '800 Practice Questions',
                desc: '8 comprehensive element banks with 100 questions each'
              },
              {
                icon: <BookOpen size={32} />,
                title: 'Detailed Explanations',
                desc: 'In-depth rationales for every question to reinforce your understanding'
              },
              {
                icon: <Clock size={32} />,
                title: 'Practice Mode',
                desc: 'Study at your own pace with unlimited access to all elements'
              },
              {
                icon: <CheckCircle size={32} />,
                title: 'Lifetime Access',
                desc: 'Unlimited access to all 8 element question banks forever'
              }
            ].map((feature, idx) => (
              <div key={idx} style={{
                background: '#f8f9fa',
                padding: '32px',
                borderRadius: '16px',
                textAlign: 'center'
              }}>
                <div style={{
                  color: '#8b5cf6',
                  marginBottom: '20px',
                  display: 'flex',
                  justifyContent: 'center'
                }}>
                  {feature.icon}
                </div>
                <h3 style={{
                  fontSize: '1.25rem',
                  fontWeight: '700',
                  color: '#1e293b',
                  marginBottom: '12px'
                }}>
                  {feature.title}
                </h3>
                <p style={{
                  color: '#64748b',
                  lineHeight: '1.6'
                }}>
                  {feature.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Element Banks Section */}
      <div style={{ padding: '80px 24px', background: '#f8f9fa' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <h2 style={{
            fontSize: '2.5rem',
            fontWeight: '800',
            color: '#1e293b',
            textAlign: 'center',
            marginBottom: '16px'
          }}>
            8 Element Question Banks
          </h2>
          <p style={{
            fontSize: '1.125rem',
            color: '#64748b',
            textAlign: 'center',
            marginBottom: '60px',
            maxWidth: '700px',
            margin: '0 auto 60px'
          }}>
            Practice questions organized by element to strengthen your knowledge systematically
          </p>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))',
            gap: '24px'
          }}>
            {ELEMENT_TILES.map((element, idx) => (
              <div
                key={element.id}
                style={{
                  background: 'white',
                  borderRadius: '16px',
                  padding: '28px',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                  border: `2px solid ${element.color}20`,
                  position: 'relative',
                  opacity: 0.7
                }}
              >
                {/* Lock Icon */}
                <div style={{
                  position: 'absolute',
                  top: '20px',
                  right: '20px',
                  background: 'rgba(100, 116, 139, 0.1)',
                  borderRadius: '50%',
                  padding: '10px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <Lock size={20} color="#64748b" />
                </div>

                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  marginBottom: '16px'
                }}>
                  <div style={{
                    width: '50px',
                    height: '50px',
                    borderRadius: '12px',
                    background: element.bgColor,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <BookOpen size={28} color={element.color} />
                  </div>
                  <h3 style={{
                    fontSize: '1.125rem',
                    fontWeight: '700',
                    color: '#1e293b',
                    margin: 0,
                    flex: 1
                  }}>
                    {element.title}
                  </h3>
                </div>

                <p style={{
                  fontSize: '0.875rem',
                  color: '#64748b',
                  marginBottom: '20px',
                  lineHeight: '1.6'
                }}>
                  {element.description}
                </p>

                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  paddingTop: '16px',
                  borderTop: '1px solid #e2e8f0'
                }}>
                  <span style={{
                    fontSize: '0.875rem',
                    fontWeight: '600',
                    color: '#94a3b8'
                  }}>
                    <Lock size={16} style={{ display: 'inline', marginRight: '6px' }} />
                    Locked
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DerivativesElementQuestionBankSalesPage;
